import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import clsx from 'clsx';

import pikko3d from '../../../../../styles/img/Pikko3D.svg';

export const PikkoBulle = ({ message, steps, currentStepIndex = 0 }) => {
  const [displayedMessage, setDisplayedMessage] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const typingIntervalRef = useRef(null);
  const timeoutsRef = useRef([]);

  const activeMessage = useMemo(() => {
    if (steps?.length > 0 && currentStepIndex < steps.length) {
      return steps[currentStepIndex].message;
    }
    return message || '';
  }, [message, steps, currentStepIndex]);

  const pikkoImage = useMemo(() => {
    return steps?.[currentStepIndex]?.pikkoImage || pikko3d;
  }, [steps, currentStepIndex]);

  const clearAllTimeouts = useCallback(() => {
    if (typingIntervalRef.current) {
      clearInterval(typingIntervalRef.current);
    }
    for (const timeout of timeoutsRef.current) {
      clearTimeout(timeout);
    }
    timeoutsRef.current = [];
  }, []);

  const typeMessage = useCallback(() => {
    let index = 0;
    clearInterval(typingIntervalRef.current);

    typingIntervalRef.current = setInterval(() => {
      if (index < activeMessage.length) {
        setDisplayedMessage(activeMessage.substring(0, index + 1));
        index++;
      } else {
        clearInterval(typingIntervalRef.current);
      }
    }, 35);
  }, [activeMessage]);

  useEffect(() => {
    clearAllTimeouts();
    setDisplayedMessage('');
    setIsVisible(false);
    setIsThinking(true);

    const visibilityTimer = setTimeout(() => {
      setIsVisible(true);
    }, 30);

    const thinkingTimer = setTimeout(() => {
      setIsThinking(false);
      typeMessage();
    }, 800);

    timeoutsRef.current = [visibilityTimer, thinkingTimer];

    return clearAllTimeouts;
  }, [typeMessage, clearAllTimeouts]);

  const isTyping = displayedMessage.length < activeMessage.length;

  return (
    <div className="flex items-center justify-start gap-4">
      <img
        src={pikkoImage}
        alt="pikko3d"
        className={clsx(
          'h-[80px]',
          'transition-opacity',
          'duration-300',
          isVisible ? 'opacity-100' : 'opacity-0'
        )}
      />
      <div
        className={clsx(
          'p-4',
          'min-h-[60px]',
          'flex',
          'items-center',
          'justify-center',
          'text-left',
          'bg-white',
          'rounded-xl',
          'border',
          'border-gray-200',
          'w-fit',
          'max-w-[220px]',
          'text-[12px]',
          '!rounded-bl-none',
          'relative',
          'transform',
          'translate-y-[10px]',
          'geomanistMedium',
          'transition-all',
          'duration-300',
          isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'
        )}
      >
        <div className="whitespace-pre-wrap">
          {isThinking ? (
            <span className="text-gray-600">...</span>
          ) : (
            <>
              {displayedMessage}
              {isTyping && (
                <span className="typing-cursor animate-pulse">|</span>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

